<div class="kpi-charts-expanded-panel-wrapper">
  <expanded-panel (closed)="onExpandedPanelCollapse()" (closedAll)="onExpandedPanelClose()">
    <div expanded-panel-header-content>
      <kpi-charts-panel-header
        *ngIf="displayValues$ | async as displayValues"
        [kpi]="selectedKpiByKey.kpi"
        [secondaryValue]="displayValues.kpiDisplaySecondaryValue"
        [protocolNumber]="protocolNumber"
        [displayUnit]="displayValues.kpiDisplayUnit"
        [displayValue]="displayValues.kpiDisplayValue"
      />
    </div>

    <chips-list expanded-panel-chips [filterService]="kpiChartFilterService"> </chips-list>
    <grid-panel-container
      #expandedPanelBody
      expanded-panel-body
      [cols]="2"
      [rowHeight]="(responsiveGridRowHeight$ | async) ?? '290px'"
      [gutterSize]="'16px'"
    >
      <ng-container *ngIf="kpiChartData$ | async as chartData">
        <ng-container *ngFor="let chartConfig of kpiChartsConfig">
          <ng-container [ngSwitch]="chartConfig.type">
            <ng-container *ngSwitchCase="'trendchart'">
              <drilldown-chart-container
                *gridTileItem="{ colspan: 1, rowspan: 1 }"
                [size]="'medium'"
                [title]="
                  formatChartTitle(
                    chartData.charts.trendchart.titleSuffix,
                    chartData.charts.trendchart.identifier
                  )
                "
                [infoText]="chartData.charts.trendchart.tooltipText"
                [isLoading]="isLoadingState"
                (expandChartClicked)="openExpandedView(chartConfig)"
              >
                <trend-chart
                  chart-element
                  *ngIf="!isLoadingState && (reactiveTrendChartData$ | async) as reactiveTrendData"
                  [trendChartData]="reactiveTrendData"
                  [size]="chartSize"
                  [kpiKey]="selectedKpiByKey.key"
                  [style.height.px]="
                    getTrendChartHeight(
                      (responsiveChartHeightPx$ | async) ?? 225,
                      reactiveTrendData?.hasToggle
                    )
                  "
                />
              </drilldown-chart-container>
            </ng-container>

            <ng-container *ngSwitchCase="'keyMilestonesTable'">
              <ng-container *ngIf="chartData.charts.keyMilestonesTable as keyMilestonesTable">
                <ng-container *ngIf="tableRowspan$ | async as currentRowspan">
                  <ng-container *ngIf="dynamicRowHeight$ | async as tableHeight">
                    <div
                      *gridTileItem="{ colspan: 2, rowspan: currentRowspan }"
                      class="dynamic-table-wrapper"
                      [style.height]="tableHeight"
                    >
                      <drilldown-chart-container
                        [title]="keyMilestonesTable.title"
                        [infoText]="keyMilestonesTable.tooltipInfo"
                        [size]="'medium'"
                        [isLoading]="isLoadingState"
                        [showEnlargeButton]="false"
                        class="dynamic-table-container"
                        (expandChartClicked)="openExpandedView(chartConfig)"
                      >
                        <drilldown-table
                          chart-element
                          [data]="keyMilestonesTable"
                          [size]="chartSize"
                          [style.height]="chartSize === 'medium' ? '90%' : null"
                        ></drilldown-table>
                      </drilldown-chart-container>
                    </div>
                  </ng-container>
                </ng-container>
              </ng-container>
            </ng-container>

            <ng-container *ngSwitchCase="'bar'">
              <ng-container *ngIf="getBarChartData(chartConfig.id) as barChartData">
                <drilldown-chart-container
                  *gridTileItem="{ colspan: 1, rowspan: 1 }"
                  [size]="'medium'"
                  [title]="formatChartTitle(barChartData.titleSuffix, barChartData.identifier)"
                  [infoText]="barChartData.tooltipText"
                  [isLoading]="isLoadingState"
                  (expandChartClicked)="openExpandedView(chartConfig)"
                >
                  <chart
                    chart-element
                    *ngIf="!isLoadingState"
                    [chartTitle]="''"
                    [field]="barChartData.fieldFilter"
                    [filterService]="kpiChartFilterService"
                    [sortedData]="barChartData.values"
                    [chartOption]="bar"
                    [kpiChartSize]="chartSize"
                    [kpiOnTrack]="barChartData.onTrack"
                    [kpiOnTarget]="barChartData.onTarget"
                    [isPercent]="barChartData.config?.isPercent ?? false"
                    [showAllKeys]="true"
                    [kpiAxisKeysShownDefault]="barChartData.yAxisLabelCount?.medium"
                    [kpiXAxisLabel]="barChartData.xAxisLabel"
                    [kpiChartIdentifier]="barChartData.identifier"
                    [hasXaxisAggLabel]="barChartData.hasXAxisAggLabel"
                    [style.height.px]="(responsiveChartHeightPx$ | async) ?? 250"
                  ></chart>
                </drilldown-chart-container>
              </ng-container>
            </ng-container>

            <ng-container *ngSwitchCase="'pie'">
              <ng-container *ngIf="getPieChartData(chartConfig.id) as pieChartData">
                <drilldown-chart-container
                  *gridTileItem="{ colspan: 1, rowspan: 1 }"
                  [size]="'medium'"
                  [title]="formatChartTitle(pieChartData.titleSuffix, pieChartData.identifier)"
                  [infoText]="pieChartData.tooltipText"
                  [isLoading]="isLoadingState"
                  (expandChartClicked)="openExpandedView(chartConfig)"
                >
                  <chart
                    chart-element
                    *ngIf="!isLoadingState"
                    [sortedData]="pieChartData.values"
                    [field]="pieChartData.fieldFilter"
                    [filterService]="kpiChartFilterService"
                    [chartOption]="pie"
                    [kpiChartSize]="chartSize"
                    [colorMap]="colorMap"
                    [showAllKeys]="true"
                    [kpiChartIdentifier]="pieChartData.identifier"
                    [style.height.px]="(responsiveChartHeightPx$ | async) ?? 250"
                  ></chart>
                </drilldown-chart-container>
              </ng-container>
            </ng-container>

            <ng-container *ngSwitchCase="'toggleChart'">
              <ng-container
                *ngIf="
                  getReactiveToggleChartData(chartConfig.id) | async as reactiveToggleChartData
                "
              >
                <drilldown-chart-container
                  *gridTileItem="{ colspan: 1, rowspan: 1 }"
                  [title]="
                    formatChartTitle(
                      reactiveToggleChartData.titleSuffix,
                      reactiveToggleChartData.identifier
                    )
                  "
                  [infoText]="reactiveToggleChartData.tooltipText"
                  [size]="'medium'"
                  [isLoading]="isLoadingState"
                  (expandChartClicked)="openExpandedView(chartConfig)"
                >
                  <drilldown-toggle-chart
                    chart-element
                    *ngIf="!isLoadingState"
                    [filterService]="kpiChartFilterService"
                    [toggleChartData]="reactiveToggleChartData"
                    [chartSize]="chartSize"
                    [kpiKey]="selectedKpiByKey.key"
                    [style.height.px]="(responsiveChartHeightPx$ | async) ?? 225"
                  ></drilldown-toggle-chart>
                </drilldown-chart-container>
              </ng-container>
            </ng-container>

            <ng-container *ngSwitchCase="'groupedBar'">
              <ng-container *ngIf="getGroupedBarChartData(chartConfig.id) as groupedBarChartData">
                <drilldown-chart-container
                  *gridTileItem="{ colspan: 1, rowspan: 1 }"
                  [size]="'medium'"
                  [title]="
                    formatChartTitle(
                      groupedBarChartData.titleSuffix,
                      groupedBarChartData.identifier
                    )
                  "
                  [infoText]="groupedBarChartData.tooltipText"
                  [isLoading]="isLoadingState"
                  (expandChartClicked)="openExpandedView(chartConfig)"
                >
                  <grouped-bar-chart
                    chart-element
                    *ngIf="!isLoadingState"
                    [field]="groupedBarChartData.fieldFilter"
                    [filterService]="kpiChartFilterService"
                    [sortedData]="groupedBarChartData.values"
                    [chartOption]="groupedBar"
                    [kpiChartSize]="chartSize"
                    [kpiOnTrack]="groupedBarChartData.onTrack"
                    [kpiOnTarget]="groupedBarChartData.onTarget"
                    [isPercent]="groupedBarChartData.config?.isPercent ?? false"
                    [showAllKeys]="true"
                    [kpiAxisKeysShownDefault]="groupedBarChartData.yAxisLabelCount?.medium"
                    [kpiXAxisLabel]="groupedBarChartData.xAxisLabel"
                    [kpiChartIdentifier]="groupedBarChartData.identifier"
                    [style.height.px]="(responsiveChartHeightPx$ | async) ?? 250"
                  ></grouped-bar-chart>
                </drilldown-chart-container>
              </ng-container>
            </ng-container>

            <ng-container *ngSwitchCase="'timeCycleChart'">
              <ng-container *ngIf="chartData.charts.timeCycleChart as timeCycleChart">
                <drilldown-chart-container
                  *gridTileItem="{ colspan: 2, rowspan: 1 }"
                  [title]="timeCycleChart.title"
                  [infoText]="timeCycleChart.tooltipText"
                  [showEnlargeButton]="false"
                  [size]="'medium'"
                  [isLoading]="isLoadingState"
                >
                  <time-cycle-chart
                    chart-element
                    [chartData]="timeCycleChart"
                    [size]="'medium'"
                    *ngIf="!isLoadingState"
                    [style.height.px]="(responsiveChartHeightPx$ | async) ?? 225"
                  >
                  </time-cycle-chart>
                </drilldown-chart-container>
              </ng-container>
            </ng-container>
          </ng-container>
        </ng-container>
      </ng-container>
    </grid-panel-container>
  </expanded-panel>
</div>
